import { Contract } from 'cpfr-common';
import { ValidatedSelect } from '@cp-shared-19/frontend-ui';
import { Layout, Paragraph } from '@vwfs-bronson/bronson-react';
import { useField } from 'formik';
import { kebabCase, toInteger, uniqBy } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DateInput,
    YearInput,
    EmailInput,
    PhonenumberInput,
    TextInput,
    CountrySelection,
    MonthSelection,
} from '../../simple-form-components';

export type DocumentsFormProps = {
    isSMECustomer?: boolean;
    contractsSelected?: Contract[];
    defaultType: string;
};

export const DocumentsForm: React.FC<DocumentsFormProps> = ({ contractsSelected = [], defaultType, isSMECustomer }) => {
    const { t } = useTranslation('request');
    const { t: myDocumentsT } = useTranslation('my-documents');
    const translationPrefix = 'category-selection.documents';
    const translate = (key: string): string => {
        return t(`${translationPrefix}.${key}`);
    };

    const [documentTypeField] = useField('documents.documentType');
    const [selectedStartDate] = useField('documents.periodStartDate');
    const [selectedStartExitDate] = useField('documents.exitDate');

    const parseDate = (dateToParse: string, offsetD?: number, offsetM?: number, offsetY?: number): Date => {
        const parts = dateToParse.match(/(\d+)/g);
        return parts
            ? new Date(
                  toInteger(parts[2]) + (offsetY ?? 0),
                  toInteger(parts[1]) + (offsetM ?? 0) - 1,
                  toInteger(parts[0]) + (offsetD ?? 0),
              )
            : new Date();
    };

    const minEndDate = selectedStartDate.value ? parseDate(selectedStartDate.value, 1).toISOString() : '';
    // const calculatedDate = selectedStartDate.value ? parseDate(selectedStartDate.value, -1, 12).toISOString() : '';
    const today = new Date();
    today.setDate(today.getDate() - 1);
    const maxStartDate = today.toISOString();
    const minExitEndDate = selectedStartExitDate.value ? parseDate(selectedStartExitDate.value, 1).toISOString() : '';
    const maxExitEndDate = selectedStartExitDate.value ? parseDate(selectedStartExitDate.value, 30).toISOString() : '';

    const currentYear = today.getFullYear();
    const invoiceYears = Array.from({ length: 3 }, (_, i) => currentYear - i).map((year) => year.toString());

    const desiredTaxStatus = ['Amortissements non déductibles', 'Avantages en nature'];
    const reasonToLeave = ['Motif professionnel', 'Motif personnel'];

    const getDescriptionText = (value: string) => {
        const desc = translate(`description.${kebabCase(value)}`);
        return desc.includes(`description.${kebabCase(value)}`) ? '' : desc;
    };

    const allowedProductTypes: { [key: string]: string[] | null } = {
        Echéancier: [
            'CBVO_FL',
            'CBPO_FL',
            'CBPN_FL',
            'CBVE_FL',
            'LOAB_PR',
            'ASVO_PR',
            'LOAN_FL',
            'LOAN_PR',
            'LOAO_PR',
            'ACVN_PR',
            'ACVO_PR',
            'CCCD_FL',
            'CRVN_FL',
            'CRVN_PR',
            'CRVO_FL',
            'CRVO_PR',
        ],
        'Duplicata de facture de loyer': [
            'LCDN_FL',
            'LLDN_FL',
            'LLDO_FL',
            'LOUN_FL',
            'LOUO_FL',
            'CBEQ_FL',
            'CBVO_FL',
            'CBPO_FL',
            'CBPN_FL',
            'CBVE_FL',
        ],
        'Copie de mon contrat': null,
        'Etats fiscaux - TVS': [
            'LCDN_FL',
            'LLDN_FL',
            'LLDO_FL',
            'LOUN_FL',
            'LOUO_FL',
            'CBEQ_FL',
            'CBEQ_FL',
            'CBPO_FL',
            'CBPN_FL',
            'CBVE_FL',
            'CBVO_FL',
        ],
        'Etats fiscaux - Amortissements non déductibles/ Avantages en nature': [
            'LCDN_FL',
            'LLDN_FL',
            'LLDO_FL',
            'LOUO_FL',
            'LOUN_FL',
            'CBEQ_FL',
            'CBEQ_FL',
            'CBPO_FL',
            'CBPN_FL',
            'CBVE_FL',
            'CBVO_FL',
        ],
        'Documents de cession': [
            'CBVO_FL',
            'CBPO_FL',
            'CBPN_FL',
            'CBVE_FL',
            'LOAB_PR',
            'ASVO_PR',
            'LOAN_FL',
            'LOAN_PR',
            'LOAO_PR',
        ],
        'Duplicata de carte grise': [
            'CBVO_FL',
            'CBPO_FL',
            'CBPN_FL',
            'CBVE_FL',
            'LOAB_PR',
            'ASVO_PR',
            'LOAN_FL',
            'LOAN_PR',
            'LOAO_PR',
            'LCDN_FL',
            'LLDN_FL',
            'LLDN_PR',
            'LLDO_FL',
            'LLDO_PR',
            'LOUN_PR',
            'LOUO_PR',
            'LOUN_FL',
            'LOUO_FL',
        ],
        'Autorisation de sortie de territoire': [
            'CBPO_FL',
            'CBPN_FL',
            'CBVE_FL',
            'CBVO_FL',
            'LOAB_PR',
            'ASVO_PR',
            'LOAN_FL',
            'LOAN_PR',
            'LOAO_PR',
            'LCDN_FL',
            'LLDN_FL',
            'LLDN_PR',
            'LLDO_FL',
            'LLDO_PR',
            'LOUN_PR',
            'LOUO_PR',
            'LOUN_FL',
            'LOUO_FL',
        ],
    };

    const hasUnpaidAmount = useMemo<boolean>(
        () => !!contractsSelected.find(({ amountUnpaidNotPaid }) => (amountUnpaidNotPaid || 0) > 0),
        [contractsSelected],
    );

    const restrictedDocumentTypes = useMemo<string[]>(() => {
        if (hasUnpaidAmount) {
            return ['Duplicata de carte grise', 'Autorisation de sortie de territoire', 'Documents de cession'];
        }

        return [];
    }, [hasUnpaidAmount]);

    const getAvailableDocumentTypes = () => {
        const allowedDocuments: string[] = Object.keys(allowedProductTypes);
        const selectedProductTypes = uniqBy(contractsSelected, 'productType').map((item) => item.productType);
        return allowedDocuments
            .filter((doc) => !restrictedDocumentTypes.includes(doc))
            .filter(
                (doc) =>
                    !allowedProductTypes[doc] ||
                    selectedProductTypes.every(
                        (productType) =>
                            allowedProductTypes[doc] &&
                            (allowedProductTypes[doc] as string[]).includes(productType || ''),
                    ),
            );
    };

    const availableDocumentTypes = getAvailableDocumentTypes();
    const noComment = ['Copie de mon contrat', 'Duplicata de facture de loyer', 'Echéancier'].includes(
        documentTypeField.value || '',
    );

    return (
        <>
            <Layout.Item>
                <ValidatedSelect
                    className="c-form-field--no-floating"
                    isMandatory={true}
                    requiredText=" *"
                    name={'documents.documentType'}
                    selectItems={availableDocumentTypes.map((docType) => ({
                        value: docType,
                        label: docType,
                    }))}
                    label={translate('document-type')}
                    emptyByDefault={!defaultType}
                />
            </Layout.Item>
            {!!documentTypeField.value && (
                <>
                    <Layout.Item>
                        <p>{getDescriptionText(documentTypeField.value)}</p>
                    </Layout.Item>
                    {documentTypeField.value === 'Duplicata de facture de loyer' && isSMECustomer && (
                        <Layout.Item>
                            <Paragraph>
                                <strong>{myDocumentsT('invoice.header')}</strong>
                                <br />
                                <strong>{myDocumentsT('invoice.description')}</strong>
                                <br />
                                <strong>{myDocumentsT('invoice.description2')}</strong>
                            </Paragraph>
                            <ul className="u-mb">
                                <li>
                                    <strong>
                                        <span
                                            className="u-mb"
                                            dangerouslySetInnerHTML={{ __html: myDocumentsT('invoice.link-1') }}
                                        />
                                    </strong>
                                </li>
                                <li>
                                    <strong>
                                        <span
                                            className="u-mb"
                                            dangerouslySetInnerHTML={{ __html: myDocumentsT('invoice.link-2') }}
                                        />
                                    </strong>
                                </li>
                            </ul>
                        </Layout.Item>
                    )}
                    <Layout.Item>
                        <EmailInput
                            label={translate('email')}
                            name={'documents.email'}
                            isMandatory={true}
                            inputId="email-documents-form"
                        />
                    </Layout.Item>
                    <Layout.Item>
                        <PhonenumberInput
                            label={translate('phonenumber')}
                            name={'documents.phonenumber'}
                            isMandatory={false}
                            inputId="phone-number-documents-form"
                        />
                    </Layout.Item>
                    {documentTypeField.value === 'Duplicata de facture de loyer' && (
                        <>
                            <Layout.Item default="1/2">
                                <MonthSelection name={'documents.invoiceMonth'} label={translate('invoice-month')} />
                            </Layout.Item>
                            <Layout.Item default="1/2">
                                <ValidatedSelect
                                    className="c-form-field--no-floating"
                                    isMandatory={true}
                                    requiredText=" *"
                                    name={'documents.invoiceYear'}
                                    selectItems={invoiceYears.map((year) => ({
                                        value: year,
                                        label: year,
                                    }))}
                                    label={translate('invoice-year')}
                                    emptyByDefault
                                />
                            </Layout.Item>
                        </>
                    )}
                    {documentTypeField.value === 'Etats fiscaux - TVS' && (
                        <Layout.Item>
                            <YearInput
                                label={translate('year')}
                                name={'documents.desiredYear'}
                                isMandatory={true}
                                inputId="desired-year-documents-form"
                            />
                        </Layout.Item>
                    )}
                    {documentTypeField.value ===
                        'Etats fiscaux - Amortissements non déductibles/ Avantages en nature' && (
                        <>
                            <Layout.Item>
                                <ValidatedSelect
                                    className="c-form-field--no-floating"
                                    aria-required={true}
                                    isMandatory={true}
                                    requiredText=" *"
                                    name={'documents.desiredTaxStatus'}
                                    selectItems={desiredTaxStatus.map((taxStatus) => ({
                                        value: taxStatus,
                                        label: taxStatus,
                                    }))}
                                    label={translate('desired-tax-status')}
                                    placeholder={translate('tax-status-placeholder')}
                                />
                            </Layout.Item>
                            <Layout.Item>
                                <DateInput
                                    maxDate={maxStartDate}
                                    label={translate('period-start-date')}
                                    name={'documents.periodStartDate'}
                                    aria-required={true}
                                    isMandatory={true}
                                    className="c-form-field--no-floating"
                                />
                            </Layout.Item>
                            <Layout.Item>
                                <DateInput
                                    minDate={minEndDate}
                                    label={translate('period-end-date')}
                                    name={'documents.periodEndDate'}
                                    aria-required={true}
                                    isMandatory={true}
                                    className="c-form-field--no-floating"
                                    // value={calculatedDate}
                                />
                            </Layout.Item>
                        </>
                    )}
                    {documentTypeField.value === 'Autorisation de sortie de territoire' && (
                        <>
                            <Layout.Item>
                                <DateInput
                                    label={translate('exit-date')}
                                    name={'documents.exitDate'}
                                    aria-required={true}
                                    isMandatory={true}
                                    className="c-form-field--no-floating"
                                />
                            </Layout.Item>
                            <Layout.Item>
                                <DateInput
                                    label={translate('return-date')}
                                    name={'documents.returnDate'}
                                    minDate={minExitEndDate}
                                    maxDate={maxExitEndDate}
                                    className="c-form-field--no-floating"
                                />
                            </Layout.Item>
                            <Layout.Item>
                                <CountrySelection
                                    name={'documents.countryOfDestination'}
                                    label={translate('country-of-destination')}
                                    selectId="country-of-destination-documents-form"
                                ></CountrySelection>
                            </Layout.Item>
                            <Layout.Item>
                                <ValidatedSelect
                                    aria-required={true}
                                    required={true}
                                    className="c-form-field--no-floating"
                                    isMandatory={true}
                                    name={'documents.reason'}
                                    selectItems={reasonToLeave.map((reason) => ({
                                        value: reason,
                                        label: reason,
                                    }))}
                                    label={translate('reason')}
                                    emptyByDefault
                                    requiredText=" *"
                                />
                            </Layout.Item>
                        </>
                    )}
                    {!noComment && (
                        <Layout.Item>
                            <TextInput label={translate('comment')} name={'documents.comment'} isMandatory={false} />
                        </Layout.Item>
                    )}
                </>
            )}
        </>
    );
};
